/**
 * <recipe-filter> — filtre de recettes côté navigateur (page listing du blog).
 *
 * Reproduit la navigation de Diaspora : onglets de TYPE (sélection unique) en haut
 * + colonne de FACETTES (multi-sélection : Produit, Difficulté…) à gauche. Cliquer
 * un onglet ou cocher une facette AFFICHE / MASQUE les cartes déjà présentes dans la
 * page — AUCUN appel réseau.
 *
 * Progressive enhancement :
 *   - Sans JS, la colonne de facettes reste `hidden` (aucun contrôle mort) et les
 *     onglets de type sont de vrais liens `/tagged/` (filtrage natif Shopify). Le JS
 *     révèle les facettes et intercepte les onglets pour filtrer sans rechargement.
 *   - Respecte prefers-reduced-motion (transitions pilotées par le CSS ; le JS ne
 *     fait que basculer des classes).
 *
 * Modèle de jetons : chaque carte porte `data-facets="type:douceurs produit:citron-noir
 * difficulte:facile"`. Un onglet actif exige `type:<valeur>` ; une facette exige, pour
 * chaque groupe coché, AU MOINS une valeur (OU intra-groupe, ET inter-groupes).
 *
 * Sélecteurs attendus (posés par sections/blog.liquid) :
 *   [data-recipe-tabs] > a[data-type]      onglets de type ("" = Tout)
 *   [data-recipe-facets]                   colonne/panneau des facettes (hidden sans JS)
 *   [data-recipe-facets] input[type=checkbox][data-group][value]
 *   [data-recipe-grid] > * [data-facets]   les cartes (li)
 *   [data-recipe-count]                    compteur de résultats (gabarit via data-*)
 *   [data-recipe-empty]                    message « aucun résultat » (hidden au départ)
 *   [data-recipe-reset]                    bouton « tout effacer » (optionnel)
 *   [data-recipe-more] / [data-recipe-more-wrap]  bouton « Afficher plus » (hidden sans JS)
 *
 * Affichage progressif : la page contient TOUTES les recettes (sections/blog.liquid,
 * `paginate … by 250`) pour que le filtre porte sur l'ensemble et pas sur une seule
 * page. On n'en montre que `data-batch` à la fois PARMI CELLES QUI CORRESPONDENT ;
 * « Afficher plus » en révèle `data-batch` de plus. Changer de filtre repart du début.
 * Les cartes masquées (`hidden`) ne téléchargent pas leur image (loading="lazy").
 *   [data-recipe-open] / [data-recipe-close] / [data-recipe-done]  drawer mobile
 *   [data-recipe-backdrop]                 fond assombri du drawer mobile
 */
class RecipeFilter extends HTMLElement {
  connectedCallback() {
    this.tabsEl = this.querySelector('[data-recipe-tabs]');
    this.facetsEl = this.querySelector('[data-recipe-facets]');
    this.grid = this.querySelector('[data-recipe-grid]');
    this.countEl = this.querySelector('[data-recipe-count]');
    this.emptyEl = this.querySelector('[data-recipe-empty]');
    if (!this.grid) return;

    this.items = Array.from(this.grid.children);
    this.activeType = '';
    this.activeFacets = new Map(); // groupe -> Set(valeurs)
    this.batch = parseInt(this.dataset.batch, 10) || 12;
    this.limit = this.batch;
    this.moreWrap = this.querySelector('[data-recipe-more-wrap]');
    this.moreBtn = this.querySelector('[data-recipe-more]');

    // Révèle la colonne de facettes (masquée sans JS pour n'exposer aucun contrôle mort).
    if (this.facetsEl) this.facetsEl.hidden = false;

    // -- État initial depuis l'URL (filtres partageables + bouton Retour) --
    this.readFromUrl();
    this.syncControls();
    this.apply();

    // -- Écouteurs --
    this.onTabClick = (e) => this.handleTabClick(e);
    this.onFacetChange = (e) => this.handleFacetChange(e);
    this.onPopState = () => { this.readFromUrl(); this.syncControls(); this.limit = this.batch; this.apply(); };

    if (this.tabsEl) this.tabsEl.addEventListener('click', this.onTabClick);
    if (this.facetsEl) this.facetsEl.addEventListener('change', this.onFacetChange);
    window.addEventListener('popstate', this.onPopState);

    this.moreBtn?.addEventListener('click', () => this.showMore());

    const reset = this.querySelector('[data-recipe-reset]');
    if (reset) reset.addEventListener('click', () => this.reset());

    this.setupDrawer();
  }

  disconnectedCallback() {
    this.tabsEl?.removeEventListener('click', this.onTabClick);
    this.facetsEl?.removeEventListener('change', this.onFacetChange);
    window.removeEventListener('popstate', this.onPopState);
  }

  // ---------------------------------------------------------------- filtrage

  handleTabClick(e) {
    const tab = e.target.closest('a[data-type]');
    if (!tab || !this.tabsEl.contains(tab)) return;
    e.preventDefault(); // on filtre sur place au lieu de charger /tagged/
    this.activeType = tab.dataset.type || '';
    this.syncTabs();
    this.limit = this.batch;
    this.apply();
    this.writeToUrl();
  }

  handleFacetChange(e) {
    const input = e.target;
    if (!input.matches('input[type="checkbox"][data-group]')) return;
    const group = input.dataset.group;
    const value = input.value;
    let set = this.activeFacets.get(group);
    if (!set) { set = new Set(); this.activeFacets.set(group, set); }
    if (input.checked) set.add(value);
    else set.delete(value);
    if (set.size === 0) this.activeFacets.delete(group);
    this.limit = this.batch;
    this.apply();
    this.writeToUrl();
  }

  matches(item) {
    const tokens = new Set((item.dataset.facets || '').split(/\s+/).filter(Boolean));
    if (this.activeType && !tokens.has('type:' + this.activeType)) return false;
    for (const [group, set] of this.activeFacets) {
      if (set.size === 0) continue;
      let hit = false;
      for (const value of set) {
        if (tokens.has(group + ':' + value)) { hit = true; break; }
      }
      if (!hit) return false;
    }
    return true;
  }

  apply() {
    let matched = 0;
    for (const item of this.items) {
      if (this.matches(item)) {
        matched += 1;
        item.hidden = matched > this.limit; // correspond, mais au-delà du lot affiché
      } else {
        item.hidden = true;
      }
    }
    this.updateCount(matched);
    if (this.emptyEl) this.emptyEl.hidden = matched !== 0;
    if (this.moreWrap) this.moreWrap.hidden = matched <= this.limit;
  }

  showMore() {
    // Première carte du nouveau lot : on y place le focus (clavier / lecteur d'écran)
    // pour que l'utilisateur ne reste pas sur un bouton qui peut disparaître.
    const firstNew = this.items.filter((item) => this.matches(item))[this.limit];
    this.limit += this.batch;
    this.apply();
    firstNew?.querySelector('.recipe-card__title a')?.focus();
  }

  updateCount(n) {
    if (!this.countEl) return;
    const one = this.countEl.getAttribute('data-count-one') || '{{ count }}';
    const other = this.countEl.getAttribute('data-count-other') || '{{ count }}';
    const tpl = n === 1 ? one : other;
    this.countEl.textContent = tpl.replace('{{ count }}', n);
  }

  reset() {
    this.activeType = '';
    this.activeFacets.clear();
    this.limit = this.batch;
    this.syncControls();
    this.apply();
    this.writeToUrl();
  }

  // ------------------------------------------------------- synchro UI ↔ état

  syncControls() {
    this.syncTabs();
    if (this.facetsEl) {
      this.facetsEl.querySelectorAll('input[type="checkbox"][data-group]').forEach((input) => {
        const set = this.activeFacets.get(input.dataset.group);
        input.checked = !!(set && set.has(input.value));
      });
    }
  }

  syncTabs() {
    if (!this.tabsEl) return;
    this.tabsEl.querySelectorAll('a[data-type]').forEach((tab) => {
      const on = (tab.dataset.type || '') === this.activeType;
      tab.classList.toggle('is-active', on);
      if (on) tab.setAttribute('aria-current', 'true');
      else tab.removeAttribute('aria-current');
    });
  }

  // ------------------------------------------------------------------- URL

  knownGroups() {
    const set = new Set();
    if (this.facetsEl) {
      this.facetsEl.querySelectorAll('input[data-group]').forEach((i) => set.add(i.dataset.group));
    }
    return set;
  }

  readFromUrl() {
    const params = new URLSearchParams(window.location.search);
    this.activeType = params.get('type') || '';
    this.activeFacets.clear();
    // On ne lit QUE nos groupes de facettes connus : sinon un paramètre étranger
    // (?preview_theme_id, ?utm_*, …) serait pris pour une facette et masquerait tout.
    this.knownGroups().forEach((group) => {
      const value = params.get(group);
      if (value) this.activeFacets.set(group, new Set(value.split(',').filter(Boolean)));
    });
  }

  writeToUrl() {
    const params = new URLSearchParams(window.location.search);
    // On repart propre sur NOS clés (type + groupes connus) et on préserve les autres
    // (ex. preview_theme_id, utm_*).
    params.delete('type');
    this.knownGroups().forEach((g) => params.delete(g));

    if (this.activeType) params.set('type', this.activeType);
    for (const [group, set] of this.activeFacets) {
      if (set.size) params.set(group, Array.from(set).join(','));
    }
    const qs = params.toString();
    const url = window.location.pathname + (qs ? '?' + qs : '');
    window.history.replaceState({}, '', url);
  }

  // -------------------------------------------------------- drawer mobile

  setupDrawer() {
    this.openBtn = this.querySelector('[data-recipe-open]');
    this.closeBtn = this.querySelector('[data-recipe-close]');
    this.doneBtn = this.querySelector('[data-recipe-done]');
    this.backdrop = this.querySelector('[data-recipe-backdrop]');
    if (!this.openBtn || !this.facetsEl) return;

    this.openBtn.addEventListener('click', () => this.openDrawer());
    this.closeBtn?.addEventListener('click', () => this.closeDrawer());
    this.doneBtn?.addEventListener('click', () => this.closeDrawer());
    this.backdrop?.addEventListener('click', () => this.closeDrawer());

    this.addEventListener('keydown', (e) => {
      if (!this.classList.contains('is-filters-open')) return;
      if (e.key === 'Escape') { this.closeDrawer(); return; }
      if (e.key === 'Tab') this.trapFocus(e);
    });
  }

  openDrawer() {
    this.classList.add('is-filters-open');
    this.openBtn.setAttribute('aria-expanded', 'true');
    // Premier élément focusable du panneau.
    const focusable = this.focusableInPanel();
    if (focusable.length) focusable[0].focus();
  }

  closeDrawer() {
    if (!this.classList.contains('is-filters-open')) return;
    this.classList.remove('is-filters-open');
    this.openBtn.setAttribute('aria-expanded', 'false');
    this.openBtn.focus();
  }

  focusableInPanel() {
    return Array.from(
      this.facetsEl.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]), select'
      )
    ).filter((el) => el.getClientRects().length > 0);
  }

  trapFocus(event) {
    const focusable = this.focusableInPanel();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('recipe-filter')) {
  customElements.define('recipe-filter', RecipeFilter);
}
